import { useState, useEffect } from 'react'
import { enqueueSnackbar } from 'notistack'
import { format } from 'date-fns'
import { useTheme } from '@mui/material/styles'
import { Stack, Typography, IconButton, useMediaQuery } from '@mui/material'
import ReceiptIcon from '@mui/icons-material/Receipt'

import { Types } from 'service-api'
import { captureError } from 'service-api/src/shared'
import { SortOrder } from 'service-api/src/types'

import { HeadCell, Order, SharedTable } from 'table'
import { findLocationUserProfileMembership } from '@shared/memberships'
import { getBenefitOfferLabel } from './helper'
import { buildErrorMessage } from '@shared/error'
import { i18WithDefault as t } from '@shared/locales'
import { BenefitConsumptionTableFilters } from './benefit_consumption_table_filters'
import { formatInTimeZone } from 'date-fns-tz'

interface RequestOptions {
  sort?: string
  sortOrder?: SortOrder
  locationUserProfileId?: string
  invoiceStartDate?: string
  invoiceEndDate?: string
  benefitOffer?: string
  limit?: number
  status?: string
  nextToken?: string | null
}

const ROWS_PER_PAGE = [10, 25, 50, 100]
const BENEFIT = 'benefitId'
const MEMBERSHIP = 'membershipId'
const REDEEMED_DATE = 'invoiceStartDate'
const ACTIONS = 'actions'

export const BenefitConsumptionTable = ({
  locationUserProfile,
  onServiceFetch,
  onActionClick,
  hideFilters,
  isSortable,
}: {
  locationUserProfile: Types.LocationUserProfileAdminDto | Types.LocationUserProfileClientDto
  onServiceFetch?: ({
    locationUserProfileId,
    options,
  }: {
    locationUserProfileId: string
    options: RequestOptions
  }) => Promise<Types.APIBenefitConsumptionRecordAdminDtoResponse>
  onActionClick?: (benefitConsumptionRecord: Types.BenefitConsumptionRecordAdminDto) => void
  hideFilters?: boolean
  isSortable?: boolean
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [benefitConsumptionRecords, setBenefitConsumptionRecords] =
    useState<Types.APIBenefitConsumptionRecordAdminDtoResponse>()
  const [visibleBenefitConsumptionRecords, setVisibleBenefitConsumptionRecords] =
    useState<Types.BenefitConsumptionRecordAdminDto[]>()
  const [isLoading, setIsLoading] = useState(false)
  const [shouldResetPage, setShouldResetPage] = useState(false)
  const [requestOptions, setRequestOptions] = useState<RequestOptions>({
    sort: REDEEMED_DATE,
    sortOrder: SortOrder.DESC,
    status: 'ACTIVE',
    limit: 50,
  })

  const locationUserProfileMembership =
    findLocationUserProfileMembership(locationUserProfile?.locationUserProfileMemberships, true) || {}
  const isMembershipCancelled = locationUserProfileMembership?.subStatus === 'CANCELED'

  const fetchRecords = async (options: RequestOptions = {}) => {
    try {
      setShouldResetPage(true)
      setIsLoading(true)
      const filterOptions = { ...requestOptions, ...options }
      let allResults: Types.BenefitConsumptionRecordAdminDto[] = []
      let nextToken: string | null | undefined = undefined
      do {
        const currentOptions: RequestOptions = { ...filterOptions, nextToken }
        const response = await onServiceFetch?.({
          locationUserProfileId: locationUserProfile?.id || '',
          options: currentOptions,
        })
        if (response) {
          allResults = [...allResults, ...response.results]
          nextToken = response.nextToken
        }
      } while (nextToken)

      setBenefitConsumptionRecords({
        results: allResults,
        nextToken: null,
      })

      let visibleResults = allResults
      if (options.benefitOffer && options.benefitOffer !== t('global.label.all')) {
        visibleResults = allResults.filter(
          (r) => getBenefitOfferLabel(r.benefit!, r.offerId || '') === options.benefitOffer
        )
      }

      sortResults(requestOptions.sort || REDEEMED_DATE, requestOptions.sortOrder || SortOrder.DESC, visibleResults)
    } catch (err) {
      enqueueSnackbar(buildErrorMessage(err), { variant: 'error' })
      captureError(err as Error)
    } finally {
      setShouldResetPage(false)
      setIsLoading(false)
    }
  }

  const applyFilters = (filters: { benefitOffer?: string; startDate?: Date; endDate?: Date }) => {
    const options: RequestOptions = {
      benefitOffer: filters.benefitOffer,
    }
    if (filters.startDate && filters.endDate) {
      options.invoiceStartDate = `gte_${format(filters.startDate, 'yyyy-MM-dd')}_lte_${format(
        filters.endDate,
        'yyyy-MM-dd'
      )}`
    } else if (filters.startDate) {
      options.invoiceStartDate = format(filters.startDate, 'yyyy-MM-dd')
    }
    fetchRecords(options)
  }

  const clearFilters = () => {
    const options = {
      sort: REDEEMED_DATE,
      sortOrder: SortOrder.DESC,
    }
    fetchRecords(options)
  }

  const sortResults = (sort: string, sortOrder: Order, results?: Types.BenefitConsumptionRecordAdminDto[]) => {
    const sortedResults = (results || benefitConsumptionRecords?.results || []).sort((a, b) => {
      if (sort === BENEFIT) {
        const benefitOfferLabelA = getBenefitOfferLabel(a.benefit!, a.offerId || '')
        const benefitOfferLabelB = getBenefitOfferLabel(b.benefit!, b.offerId || '')
        return sortOrder === SortOrder.DESC
          ? benefitOfferLabelB.localeCompare(benefitOfferLabelA)
          : benefitOfferLabelA.localeCompare(benefitOfferLabelB)
      } else if (sort === REDEEMED_DATE) {
        return sortOrder === SortOrder.DESC
          ? new Date(b.invoiceStartDate!).getTime() - new Date(a.invoiceStartDate!).getTime()
          : new Date(a.invoiceStartDate!).getTime() - new Date(b.invoiceStartDate!).getTime()
      }
      return 0
    })
    setVisibleBenefitConsumptionRecords(sortedResults)
  }

  const handleSorting = (sort: string, order: Order, limit: number) => {
    setRequestOptions({ ...requestOptions, sort, sortOrder: order as SortOrder })
    sortResults(sort, order)
  }

  useEffect(() => {
    fetchRecords()
  }, [])

  const headCells = (): HeadCell[] => {
    return [
      {
        id: BENEFIT,
        label: t('global.label.benefit'),
        width: '25%',
        sortable: isSortable,
      },
      { id: MEMBERSHIP, label: t('global.label.membership'), width: '25%' },
      {
        id: REDEEMED_DATE,
        label: t('membership.benefit.redeemedDate'),
        width: '25%',
        sortable: isSortable,
      },
      {
        id: ACTIONS,
        label: t('global.label.actions'),
        align: 'right',
        width: '25%',
      },
    ]
  }

  const getRenderRows = (benefitConsumptionRecord: Types.BenefitConsumptionRecordAdminDto) => {
    const { benefit, offerId } = benefitConsumptionRecord
    return {
      [BENEFIT]: (
        <Typography variant="body2bold">
          {getBenefitOfferLabel(benefit!, offerId || '')} {t('membership.benefit.discount.small')}
        </Typography>
      ),
      [MEMBERSHIP]: (
        <Typography variant="body2bold">
          {benefitConsumptionRecord.locationUserProfileMembership?.membership?.displayName || ''}
        </Typography>
      ),
      [REDEEMED_DATE]: (
        <Typography variant="body2bold">
          {formatInTimeZone(benefitConsumptionRecord.invoiceStartDate!, 'UTC', 'MMM d, yyyy')}
        </Typography>
      ),
      [ACTIONS]: (
        <Stack direction="row" spacing={1} justifyContent="right">
          <IconButton color="primary" onClick={() => onActionClick?.(benefitConsumptionRecord)}>
            <ReceiptIcon />
          </IconButton>
        </Stack>
      ),
    }
  }

  return (
    <Stack>
      {!hideFilters && (
        <Stack>
          <BenefitConsumptionTableFilters
            benefitConsumptionRecords={benefitConsumptionRecords}
            onApplyFilters={applyFilters}
            onClearFilters={clearFilters}
            hideBenefitFilter={isMembershipCancelled}
          />
        </Stack>
      )}
      <SharedTable
        containerStyle={{
          '&&&': {
            padding: '16px 24px',
            border: '1px solid #E0E0E0',
          },
          '&&& .MuiTableSortLabel-root .MuiSvgIcon-root': {
            opacity: 1,
          },
        }}
        component={Stack}
        title={t('membership.benefit.tableLabel')}
        rows={visibleBenefitConsumptionRecords?.map(getRenderRows) || []}
        head={headCells()}
        defaultOrder="desc"
        defaultOrderBy={REDEEMED_DATE}
        isLoading={isLoading}
        rowsPerPageConfig={ROWS_PER_PAGE}
        hasMore={false}
        emptyTableMessage={t('global.label.noData')}
        onSort={handleSorting}
        shouldResetPage={shouldResetPage}
        isScrollable={isMobile}
      />
    </Stack>
  )
}
